// src/components/admin/AuditLogViewer.jsx
import React, { useState } from "react";
import {
  ClockIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/outline";
import { useAuditLog } from "../../hooks/user/useAuditLog";
import Loading from "../common/Loading";

/**
 * Audit Log Viewer Component
 *
 * Displays audit logs for admin actions, particularly verification changes.
 * Requirements: 11.4, 11.5
 */
const AuditLogViewer = ({ userId = null }) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [actionFilter, setActionFilter] = useState(null);

  const {
    data: auditData,
    isLoading,
    error,
    refetch,
  } = useAuditLog({
    userId,
    action: actionFilter,
    page,
    limit,
  });

  const handleActionFilterChange = (action) => {
    setActionFilter(action === "all" ? null : action);
    setPage(1);
  };

  const formatActionType = (action) => {
    switch (action) {
      case "verification_granted":
        return {
          text: "Verification Granted",
          color: "text-green-600",
          icon: CheckCircleIcon,
        };
      case "verification_revoked":
        return {
          text: "Verification Revoked",
          color: "text-red-600",
          icon: XCircleIcon,
        };
      default:
        return { text: action, color: "text-gray-600", icon: UserIcon };
    }
  };

  const totalPages = auditData?.pagination?.totalPages || 0;
  const logs = auditData?.logs || [];

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <h3 className="text-lg font-bold text-red-800 mb-2">
          Error Loading Audit Logs
        </h3>
        <p className="text-red-600 mb-4">{error.message}</p>
        <button onClick={() => refetch()} className="btn btn--secondary">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header and Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <ClockIcon className="w-5 h-5 text-deep-red" />
          <h3 className="text-lg font-bold text-dark-grey">
            {userId ? "User Audit Log" : "Admin Audit Log"}
          </h3>
        </div>

        <div className="flex items-center gap-3">
          <select
            value={actionFilter || "all"}
            onChange={(e) => handleActionFilterChange(e.target.value)}
            className="input w-full sm:w-48"
            aria-label="Filter by action type"
          >
            <option value="all">All Actions</option>
            <option value="verification_granted">Verification Granted</option>
            <option value="verification_revoked">Verification Revoked</option>
          </select>

          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="input w-full sm:w-20"
            aria-label="Items per page"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      {/* Audit Logs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {isLoading ? (
          <div className="p-8">
            <Loading />
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center">
            <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-dark-grey mb-2">
              No Audit Logs Found
            </h3>
            <p className="text-medium-grey">
              No admin actions have been recorded yet.
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-medium-grey">
                    <th className="py-3 px-4 font-medium">Timestamp</th>
                    <th className="py-3 px-4 font-medium">Action</th>
                    <th className="py-3 px-4 font-medium">Target User</th>
                    <th className="py-3 px-4 font-medium">Admin</th>
                    <th className="py-3 px-4 font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const actionInfo = formatActionType(log.action);
                    const ActionIcon = actionInfo.icon;

                    return (
                      <tr
                        key={log._id}
                        className="border-t border-gray-100 hover:bg-gray-50"
                      >
                        <td className="py-3 px-4 text-medium-grey">
                          <div className="flex items-center gap-2">
                            <ClockIcon className="w-4 h-4" />
                            <div>
                              <div className="font-medium">
                                {new Date(log.createdAt).toLocaleDateString()}
                              </div>
                              <div className="text-xs">
                                {new Date(log.createdAt).toLocaleTimeString()}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div
                            className={`flex items-center gap-2 ${actionInfo.color}`}
                          >
                            <ActionIcon className="w-4 h-4" />
                            <span className="font-medium">
                              {actionInfo.text}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <img
                              src={
                                log.targetUser?.picture || "/default-avatar.png"
                              }
                              alt={log.targetUser?.username}
                              className="w-6 h-6 rounded-full"
                            />
                            <div>
                              <div className="font-medium text-dark-grey">
                                {log.targetUser?.username}
                              </div>
                              <div className="text-xs text-medium-grey">
                                {log.targetUser?.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <img
                              src={log.admin?.picture || "/default-avatar.png"}
                              alt={log.admin?.username}
                              className="w-6 h-6 rounded-full"
                            />
                            <span className="font-medium text-dark-grey">
                              {log.admin?.username}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-medium-grey max-w-xs">
                          <div className="truncate" title={log.reason}>
                            {log.reason}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden p-4 space-y-3">
              {logs.map((log) => {
                const actionInfo = formatActionType(log.action);
                const ActionIcon = actionInfo.icon;

                return (
                  <div
                    key={log._id}
                    className="border border-gray-100 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div
                        className={`flex items-center gap-2 ${actionInfo.color}`}
                      >
                        <ActionIcon className="w-5 h-5" />
                        <span className="font-medium">{actionInfo.text}</span>
                      </div>
                      <div className="text-xs text-medium-grey text-right">
                        <div>
                          {new Date(log.createdAt).toLocaleDateString()}
                        </div>
                        <div>
                          {new Date(log.createdAt).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-medium-grey">Target:</span>
                        <img
                          src={log.targetUser?.picture || "/default-avatar.png"}
                          alt={log.targetUser?.username}
                          className="w-5 h-5 rounded-full"
                        />
                        <span className="font-medium text-dark-grey">
                          {log.targetUser?.username}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-medium-grey">Admin:</span>
                        <img
                          src={log.admin?.picture || "/default-avatar.png"}
                          alt={log.admin?.username}
                          className="w-5 h-5 rounded-full"
                        />
                        <span className="font-medium text-dark-grey">
                          {log.admin?.username}
                        </span>
                      </div>

                      <div>
                        <span className="text-medium-grey">Reason:</span>
                        <div className="text-dark-grey mt-1">{log.reason}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t border-gray-100 p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-medium-grey">
                    Page {page} of {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                      disabled={page === 1}
                      className="btn btn--ghost text-sm"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                      Previous
                    </button>
                    <button
                      onClick={() =>
                        setPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={page === totalPages}
                      className="btn btn--ghost text-sm"
                    >
                      Next
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogViewer;
